//frontend/src/features/authentication/components/ChangePasswordModal.tsx

import React, { useState } from 'react';
import { changePasswordModalStyles as styles } from '../../../shared/styles/changePasswordModalStyles';
import { ROUTE_AUTH_CHANGE_FIRST_PASSWORD } from '../../../config/routes/routeConfig';
import useI18n from '../../../shared/hooks/i18n/useI18n';

interface ChangePasswordModalProps {
	isOpen: boolean;
	onSuccess: () => void;
}

interface ChangePasswordResponse {
	message?: string;
	[key: string]: any;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
	isOpen,
	onSuccess,
}) => {
	const [newPassword, setNewPassword] = useState<string>('');
	const [confirmPassword, setConfirmPassword] = useState<string>('');
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const { t } = useI18n();

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		e.preventDefault();
		setError('');

		// Validaciones
		if (newPassword.length < 8) {
			setError(t('login.changePassword.errorMinLength'));
			return;
		}

		if (newPassword !== confirmPassword) {
			setError(t('login.changePassword.errorMismatch'));
			return;
		}

		setLoading(true);

		try {
			const response = await fetch(ROUTE_AUTH_CHANGE_FIRST_PASSWORD, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ newPassword }),
				credentials: 'include',
			});

			const data: ChangePasswordResponse = await response.json();

			if (response.ok) {

				onSuccess(); // Cerrar modal y continuar al dashboard
			} else {
				setError(data.message || t('login.changePassword.errorDefault'));
			}
		} catch {

			setError(t('login.changePassword.errorConnection'));
		} finally {
			setLoading(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className={styles.overlay}>
			<div className={styles.modalContainer}>
				<div className={styles.modal}>
					{/* Header con icono de seguridad */}
					<div className={styles.header}>
						<div className={styles.iconContainer}>
							<svg
								className={styles.icon}
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
								/>
							</svg>
						</div>
						<h2 className={styles.title}>
							{t('login.changePassword.title')}
						</h2>
						<p className={styles.subtitle}>
							{t('login.changePassword.subtitle')}
						</p>
					</div>

					{/* Formulario */}
					<form
						onSubmit={handleSubmit}
						className={styles.form}>
						{error && <div className={styles.errorMessage}>{error}</div>}

						<div className={styles.inputGroup}>
							<label
								htmlFor='newPassword'
								className={styles.inputLabel}>
								{t('login.changePassword.newPasswordLabel')}
							</label>
							<input
								id='newPassword'
								type='password'
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								className={styles.input}
								placeholder={t('login.changePassword.newPasswordPlaceholder')}
								required
								disabled={loading}
							/>
						</div>

						<div className={styles.inputGroup}>
							<label
								htmlFor='confirmPassword'
								className={styles.inputLabel}>
								{t('login.changePassword.confirmPasswordLabel')}
							</label>
							<input
								id='confirmPassword'
								type='password'
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								className={styles.input}
								placeholder={t('login.changePassword.confirmPasswordPlaceholder')}
								required
								disabled={loading}
							/>
						</div>

						<button
							type='submit'
							className={`${styles.submitButton} ${
								loading ? styles.submitButtonLoading : ''
							}`}
							disabled={loading}>
							{loading ? (
								<>
									<svg
										className={styles.loadingIcon}
										viewBox='0 0 24 24'>
										<circle
											cx='12'
											cy='12'
											r='10'
											stroke='currentColor'
											strokeWidth='4'
											fill='none'
											strokeDasharray='32'
											strokeDashoffset='32'
										/>
									</svg>
									{t('login.changePassword.submitting')}
								</>
							) : (
								t('login.changePassword.submitButton')
							)}
						</button>
					</form>

					{/* Footer con información */}
					<div className={styles.footer}>
						<p className={styles.footerText}>
							{t('login.changePassword.footerText')}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ChangePasswordModal;
